import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  message,
  Form,
  Input,
  Select,
  Modal,
  Upload,
  Pagination,
  theme,
} from "antd";
import { get, getBlob, post, put, remove } from "../../../utils/apiUtils";
import { Code } from "../../../types/types";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TranslationModal from "../../modals/TranslationModal";
import { RightOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import TabsView from "./TabsView";
import PlusIcon from "../../../assets/img/add-plusIcon.png";
import EditIcon from "../../../assets/img/editIcon.png";
import DeleteIcon from "../../../assets/img/deleteIconn.png";
import CodeHeaderActions from "./CodeHeaderActions";
import { motion } from "framer-motion";
import "./RecursiveCode.css";
import { InboxOutlined } from "@ant-design/icons";
import { SketchPicker } from "react-color";
import dayjs from "dayjs";
import { getUserRole } from "../../../hooks/getUserRoles";
import axios from "axios";

const { Dragger } = Upload;
const { Option } = Select;

const flattenCodeTree = (
  nodes: Code[],
  level: number = 0,
  parentId: string | null = null,
): any[] => {
  return nodes.flatMap((node: any) => {
    const flatNode = {
      key: node.id,
      id: node.id,
      name: node.name,
      description: node.description,
      theme: node.theme,
      is_promoted: node.is_promoted,
      hash_color: node.hash_color ?? node.hash_color,
      is_weight_enabled: node.is_weight_enabled,
      weight_min: node.weight_min,
      weight_max: node.weight_max,
      weight_default: node.weight_default,
      created_at: node.created_at,
      _createdTs: node.created_at ? new Date(node.created_at).getTime() : 0,
      level,
      parentId,
    };

    const children = node.children?.length
      ? flattenCodeTree(node.children, level + 1, node.id)
      : [];

    return [flatNode, ...children];
  });
};

const RecursiveCodeTable = () => {
  const { t } = useTranslation("docProcessingCodes");
  const { id: projectId } = useParams<{ id: string }>();
  const userRole = getUserRole();
  const [recursiveData, setRecursiveData] = useState<Code[]>([]);
  const [flatRecursiveData, setFlatRecursiveData] = useState<any[]>([]);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isChildMode, setIsChildMode] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSavingCode, setIsSavingCode] = useState<boolean>(false); // <<< NEW
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showCodeDetails, setShowCodeDetails] = useState(false);
  const [editingCode, setEditingCode] = useState<any>(null);
  const [exportModalVisible, setExportModalVisible] = useState<any>(null);
  const [importModalVisible, setImportModalVisible] = useState<any>(null);
  const [parentFieldDisabled, setParentFieldDisabled] = useState(false);
  const [filteredOutIds, setFilteredOutIds] = useState<string[]>([]);
  const [isReparenting, setIsReparenting] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [codeDataById, setCodesDataById] = useState();
  const uploadRef = useRef<any>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const snapshotRef = useRef<HTMLDivElement | null>(null);
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (projectId) fetchTree();
  }, [projectId, currentPage]);

  const fetchTree = async () => {
    setIsLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const response = await get<{ items: Code[]; total: number }>(
        `/codes?project_id=${projectId}&skip=${skip}&limit=${pageSize}`,
      );
      console.log("Fetched tree response:", response);
      if (response?.type === "success") {
        const parentItems = response.data?.items;

        setRecursiveData(parentItems);
        setTotalCount(response.data?.total || 0);
        const flattened = flattenCodeTree(parentItems);
        setFlatRecursiveData(flattened);
        setExpandedIds([]);
      } else {
        const apiErrorResponse = (response as any)?.data?.response?.data;
        message.error(
          apiErrorResponse?.detail ||
            apiErrorResponse?.message ||
            (response as any)?.data?.message ||
            "Failed to fetch codes.",
        );
      }
    } catch (error) {
      console.error("Failed to fetch tree:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpandRecursive = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const getVisibleNodes = (nodes: any[], expandedIds: string[]) => {
    const visible: any[] = [];
    const parentExpandedMap = new Map<string, boolean>();

    nodes.forEach((node) => {
      const parentId = node.parentId;
      const isParentExpanded =
        !parentId ||
        parentExpandedMap.get(parentId) ||
        expandedIds.includes(parentId);

      if (!parentId || isParentExpanded) {
        visible.push(node);
        parentExpandedMap.set(node.id, expandedIds.includes(node.id));
      }
    });

    return visible;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedKeys: React.Key[]) => {
      const singleSelect = newSelectedKeys.slice(-1);
      setSelectedRowKeys(singleSelect);
    },
  };

  const openAddCodeModal = (isChild = false) => {
    setIsChildMode(isChild);
    setEditingCode(null);
    setIsModalVisible(true);
    setSelectedColor("#000000");
    form.resetFields();
  };

  const deleteCode = async () => {
    if (!selectedRowKeys.length) return;
    const codeIdToDelete = selectedRowKeys[0];
    setIsLoading(true);
    try {
      const response = await remove<any>(`/codes/${codeIdToDelete}`, {});
      if (response.type === "success") {
        message.success("Code deleted successfully");
        setSelectedRowKeys([]);
        fetchTree();
      } else {
        message.error("Failed to delete code");
      }
    } catch (error) {
      message.error("Error occurred while deleting code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload: any = {
        name: values.name,
        description: values.description,
        project_id: projectId,
        hash_color: selectedColor,
        weight_min: values.weight_min || 0,
        weight_max: values.weight_max || 0,
        weight_default: values.weight_default || 0,
        theme: values.theme,
      };

      if (values.hasOwnProperty("parent_id")) {
        payload.parent_id = values.parent_id ?? null;
      }

      setIsSavingCode(true); // <<< NEW
      setIsLoading(true);

      let response;
      if (editingCode) {
        response = await put(`/codes/${editingCode.id}`, payload);
      } else {
        payload.parent_id = isChildMode ? selectedRowKeys[0] : null;
        response = await post("/codes/create", payload);
      }

      if (response?.type === "success") {
        message.success(editingCode ? "Code updated" : "Code added");
        setIsModalVisible(false);
        setEditingCode(null);
        fetchTree();
      } else {
        throw new Error("Failed to save code");
      }
    } catch (error) {
      if ((error as any)?.errorFields) {
        // validation error nga form – mos shfaq error message ekstra
      } else {
        message.error("Failed to save code");
      }
    } finally {
      setIsLoading(false);
      setIsSavingCode(false); // <<< NEW
    }
  };

  const viewDataSnapshot = async () => {
    setIsSnapshotLoading(true);
    setShowCodeDetails(true);

    // prit 1 tick që të renderohet DOM-i
    requestAnimationFrame(() => {
      snapshotRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setIsSnapshotLoading(false);
    });
  };

  const getAllDescendants = (id: string): string[] => {
    const children = flatRecursiveData.filter((item) => item.parentId === id);
    return children.reduce(
      (acc, child) => [...acc, child.id, ...getAllDescendants(child.id)],
      [] as string[],
    );
  };

  const handleExportCodes = async () => {
    if (!projectId) return;
    setIsLoading(true);

    try {
      const response = await getBlob<Blob>(
        `codes/export?project_id=${projectId}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Accept: "*/*",
          },
        },
      );

      if (response.type === "error") {
        throw new Error("Failed to export codes.");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "codes_export.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      message.error("Failed to export codes.");
    } finally {
      setIsLoading(false);
    }
  };

  const getFullPathName = (id: string): string => {
    let path = [];
    let current = flatRecursiveData.find((item) => item.id === id);
    while (current) {
      path.unshift(current.name);
      current = flatRecursiveData.find((item) => item.id === current.parentId);
    }
    return path.join(" > ");
  };

  const getShortenedPath = (fullPath: string, maxLevels = 3): string => {
    const parts = fullPath.split(" > ");
    if (parts.length <= maxLevels) return fullPath;
    return `... > ${parts.slice(-maxLevels).join(" > ")}`;
  };

  const fetchCodeById = async (codeId: string) => {
    const res = await get<any>(`/codes/${codeId}`);
    if (res.type === "success") {
      setCodesDataById(res.data.item);
      return res.data.item;
    }
    throw new Error("Failed to load code");
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      message.error("Please select a file first");
      return;
    }

    try {
      setIsImporting(true);
      const formData = new FormData();
      formData.append("file", importFile);

      const baseUrl = process.env.REACT_APP_TYPE_API_URL;

      const res = await axios.post(`${baseUrl}/codes/import`, formData, {
        params: { project_id: projectId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          Accept: "application/json",
        },
      });

      message.success(res.data?.message || "Codes imported successfully.");
      setImportModalVisible(false);
      setImportFile(null);
      fetchTree();
    } catch (err: any) {
      console.error(err?.response || err);
      message.error(err?.response?.data?.message || "Failed to import codes.");
    } finally {
      setIsImporting(false);
    }
  };

  // klikimi në emër/cell selekton rreshtin
  const handleRowClick = (id: string) => {
    setSelectedRowKeys((prev) => (prev[0] === id ? [] : [id]));
  };

  const hasWeight = (record: any) => record.is_weight_enabled === true;

  const togglePromoteCode = async (record: any) => {
    try {
      setIsLoading(true);

      const response = await post(`/codes/${record.id}/promote`, {});

      if (response?.type === "success") {
        message.success(
          record.is_promoted ? "Code unpromoted" : "Code promoted",
        );
        fetchTree();
      } else {
        message.error("Failed to update promotion status");
      }
    } catch (error) {
      message.error("Failed to update promotion status");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recursiveTableSection">
      <motion.div
        className="card codeHeaderCard"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        whileHover={{
          y: -4,
          boxShadow: "0px 18px 44px rgba(79, 8, 160, 0.1)",
          transition: { duration: 0.3 },
          backgroundColor: "white",
        }}
      >
        <CodeHeaderActions
          onImport={() => setImportModalVisible(true)}
          onExport={handleExportCodes}
          onSnapshot={viewDataSnapshot}
          snapshotLoading={isSnapshotLoading}
        />
      </motion.div>

      <h2 style={{ marginTop: "2rem" }}>{t("recursiveCodeTable.pageTitle")}</h2>

      {userRole !== "Data Collector" && (
        <>
          <div className="codecardActions">
            <div className="codecardActionsContent">
              <Button
                onClick={() => openAddCodeModal(false)}
                disabled={isLoading}
                style={{
                  backgroundColor: "#4A1C96",
                  border: "1px solid #4A1C96",
                  color: "#fff",
                  display: "flex",
                  fontFamily: "Poppins",
                  alignItems: "center",
                  gap: "0.5rem",
                  height: "44px",
                }}
              >
                <img
                  src={PlusIcon}
                  alt="plus icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {t("recursiveCodeTable.addCode")}
              </Button>
              <Button
                onClick={() => openAddCodeModal(true)}
                disabled={!selectedRowKeys.length || isLoading}
                style={{
                  backgroundColor: "#A072EB",
                  border: "1px solid #A072EB",
                  fontFamily: "Poppins",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  height: "44px",
                  gap: "0.5rem",
                }}
              >
                <img
                  src={PlusIcon}
                  alt="plus icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {t("recursiveCodeTable.addSubcode")}
              </Button>
            </div>

            <div className="codecardTable">
              <Button
                type="default"
                disabled={!selectedRowKeys.length || isLoading}
                onClick={async () => {
                  const selectedId = String(selectedRowKeys[0]);

                  try {
                    setIsLoading(true);

                    const item = await fetchCodeById(selectedId);

                    const hasChildren = flatRecursiveData.some(
                      (n: any) => n.parentId === item.id,
                    );
                    const descendants = getAllDescendants(item.id);

                    setEditingCode(item);
                    setIsModalVisible(true);
                    setIsChildMode(false);
                    setParentFieldDisabled(!hasChildren);
                    setIsReparenting(false);
                    setFilteredOutIds(
                      [item.id, (item as any).parent_id, ...descendants].filter(
                        Boolean,
                      ),
                    );

                    const hex = item.hash_color || "#000000";
                    setSelectedColor(hex);

                    form.setFieldsValue({
                      name: item.name,
                      description: item.description,
                      parent_id: !hasChildren
                        ? item.id
                        : ((item as any).parent_id ?? null),
                      hash_color: hex,
                      theme: item.theme,
                      weight_min: (item as any).weight_min ?? 0,
                      weight_max: (item as any).weight_max ?? 0,
                      weight_default: (item as any).weight_default ?? 0,
                    });
                  } catch (e) {
                    message.error("Failed to load code");
                  } finally {
                    setIsLoading(false);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  color: "#4A1C96",
                  height: "44px",
                  fontFamily: "Poppins",
                  border: "1px solid #4A1C96",
                }}
              >
                <img
                  src={EditIcon}
                  alt="edit icon"
                  style={{ width: 16, height: 16 }}
                />
                {t("recursiveCodeTable.edit")}
              </Button>

              <Button
                type="default"
                disabled={!selectedRowKeys.length || isLoading}
                onClick={deleteCode}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontFamily: "Poppins",
                  gap: "0.5rem",
                  color: "#A072EB",
                  height: "44px",
                  border: "1px solid #A072EB",
                }}
              >
                <img
                  src={DeleteIcon}
                  alt="delete icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {t("recursiveCodeTable.delete")}
              </Button>
            </div>
          </div>

          <TranslationModal
            isVisible={isModalVisible}
            title={
              editingCode
                ? t("recursiveCodeTable.editModalTitle")
                : isChildMode
                  ? t("recursiveCodeTable.addSubcodeModalTitle")
                  : t("recursiveCodeTable.addCodeModalTitle")
            }
            onCancel={() => {
              setIsModalVisible(false);
              setEditingCode(null);
              setIsReparenting(false);
            }}
            className="codeSubcodeModal"
            width={600}
            content={
              <>
                <Form form={form} layout="vertical">
                  <Form.Item
                    label={
                      editingCode
                        ? "Code/Subcode name"
                        : isChildMode
                          ? "Subcode name"
                          : "Code name"
                    }
                    name="name"
                    rules={[{ required: true, message: "Please enter a name" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    label="Description"
                    name="description"
                    rules={[
                      { required: true, message: "Please enter description" },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label="Code theme"
                    name="theme"
                    // rules={[
                    //   { required: true, message: "Please enter a theme" },
                    // ]}
                  >
                    <Input />
                  </Form.Item>

                  {editingCode && isReparenting && (
                    <div
                      style={{
                        background: "#FDF8FF",
                        border: "1px solid #F5B0D2",
                        padding: "1.5rem 1.5rem",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#f5ddff",
                          padding: "12px 16px",
                          borderRadius: "8px",
                          marginBottom: "16px",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#3f0071" }}>
                          Note:
                        </span>{" "}
                        <span style={{ color: "#3f0071" }}>
                          This code will become a top-level parent if submitted
                          without a selected parent.
                        </span>
                      </div>

                      <Form.Item
                        label="Parent Code"
                        name="parent_id"
                        style={{ marginBottom: "unset" }}
                      >
                        <Select
                          allowClear
                          placeholder="Select parent code"
                          showSearch
                          disabled={parentFieldDisabled}
                          optionFilterProp="children"
                          filterOption={(input: any, option: any) =>
                            (option?.children as string)
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {flatRecursiveData
                            .filter((item) => !filteredOutIds.includes(item.id))
                            .map((item) => (
                              <Option key={item.id} value={item.id}>
                                {getFullPathName(item.id)}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  )}
                  {editingCode && !isReparenting && (
                    <Button
                      type="link"
                      onClick={() => {
                        setIsReparenting(true);
                        setParentFieldDisabled(false);
                        form.setFieldsValue({ parent_id: null });
                      }}
                      style={{
                        color: "#4A1C96",
                        fontWeight: 600,
                        marginTop: "1rem",
                        marginLeft: "unset",
                        padding: "0",
                        textDecoration: "underline",
                      }}
                      className="reparentButtonInCodeModal"
                    >
                      Reparent
                    </Button>
                  )}
                  <div className="colorPickerContainer">
                    <Form.Item
                      label="Color"
                      name="hash_color"
                      style={{ padding: "1rem" }}
                    >
                      <SketchPicker
                        color={selectedColor}
                        onChangeComplete={(color) => {
                          setSelectedColor(color.hex);
                          form.setFieldsValue({ hash_color: color.hex });
                        }}
                        presetColors={[
                          "#FF6900",
                          "#FCB900",
                          "#7BDCB5",
                          "#00D084",
                          "#8ED1FC",
                          "#0693E3",
                          "#ABB8C3",
                          "#EB144C",
                          "#F78DA7",
                          "#9900EF",
                        ]}
                      />
                    </Form.Item>
                    <div className="weightInputs">
                      <Form.Item
                        label="Weight Minimum"
                        name="weight_min"
                        initialValue={0}
                      >
                        <Input type="number" min={0} />
                      </Form.Item>

                      <Form.Item
                        label="Weight Maximum"
                        name="weight_max"
                        initialValue={0}
                      >
                        <Input type="number" min={0} />
                      </Form.Item>

                      <Form.Item
                        label="Default Weight"
                        name="weight_default"
                        initialValue={0}
                      >
                        <Input type="number" min={0} />
                      </Form.Item>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "2rem",
                      display: "flex",
                      justifyContent: "center",
                      gap: "1rem",
                    }}
                  >
                    <Button
                      onClick={() => {
                        setIsModalVisible(false);
                        setEditingCode(null);
                      }}
                      className="cancelButtonInCodeModal"
                      disabled={isSavingCode}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleModalSubmit}
                      loading={isSavingCode} // <<< loader këtu
                      className="submitButtonInCodeModal"
                    >
                      Submit
                    </Button>
                  </div>
                </Form>
              </>
            }
          />
        </>
      )}

      <Modal
        title="Import Codes from Excel"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportFile(null);
        }}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setImportModalVisible(false);
              setImportFile(null);
            }}
            style={{
              backgroundColor: "white",
              border: "1px solid #4A1C96",
              color: "#4A1C96",
              fontFamily: "Poppins",
              height: "44px",
              fontSize: "14px",
              marginTop: "2rem",
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            onClick={handleImportSubmit}
            loading={isImporting}
            style={{
              backgroundColor: "#4A1C96",
              border: "1px solid #4A1C96",
              color: "#fff",
              fontFamily: "Poppins",
              height: "44px",
              fontSize: "14px",
              marginTop: "2rem",
            }}
          >
            Submit
          </Button>,
        ]}
        className="importModal"
      >
        <p style={{ marginBottom: "8px", color: "#4A1C96", fontWeight: "500" }}>
          <span style={{ color: "red" }}>*</span> Upload here
        </p>

        <Dragger
          name="file"
          accept=".xlsx,.xls"
          maxCount={1}
          beforeUpload={(file) => {
            setImportFile(file as File);
            return false;
          }}
          onRemove={() => setImportFile(null)}
          showUploadList={{ showRemoveIcon: true }}
          style={{
            background: "#fdf8ff",
            border: "2px dashed #a072eb",
            borderRadius: "10px",
            padding: "2rem",
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: "#722ED1" }} />
          </p>
          <p
            className="ant-upload-text"
            style={{ color: "#722ED1", fontWeight: 500 }}
          >
            Drag and drop files here, or{" "}
            <span style={{ textDecoration: "underline" }}>
              click to select files
            </span>
          </p>
          <p className="ant-upload-hint" style={{ color: "#888" }}>
            Excel <br /> Max file size: 5MB
          </p>
        </Dragger>
      </Modal>

      <Table
        loading={isLoading}
        rowSelection={rowSelection}
        className="codesTable"
        rowKey="id"
        columns={[
          {
            title: t("codeTable.code"),
            dataIndex: "name",
            key: "name",
            render: (text, record) => {
              const hasChildren = flatRecursiveData.some(
                (item) => item.parentId === record.id,
              );
              const isExpanded = expandedIds.includes(record.id);

              return (
                <div
                  style={{
                    paddingLeft: `${record.level * 20}px`,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRowClick(record.id)}
                >
                  {hasChildren && (
                    <RightOutlined
                      onClick={(e) => {
                        e.stopPropagation(); // mos prish selection kur hap/mbyll
                        toggleExpandRecursive(record.id);
                      }}
                      style={{
                        cursor: "pointer",
                        transform: isExpanded
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        marginRight: 8,
                        transition: "transform 0.2s ease",
                        color: "#722ED1",
                      }}
                    />
                  )}
                  {text}
                </div>
              );
            },
          },
          {
            title: t("codeTable.description"),
            dataIndex: "description",
            key: "description",
          },
          {
            title: t("codeTable.theme"),
            dataIndex: "theme",
            key: "theme",
          },
          {
            title: "",
            key: "promote",
            align: "right",
            width: 80,
            render: (_, record) => {
              const hasWeight = record.is_weight_enabled === true;
              const isPromoted = record.is_promoted === true;

              return (
                <Button
                  type="text"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (!hasWeight) return;

                    togglePromoteCode(record);
                  }}
                  style={{
                    cursor: hasWeight ? "pointer" : "not-allowed",
                  }}
                  icon={
                    isPromoted ? (
                      <StarFilled
                        style={{
                          color: hasWeight ? "#A072EB" : "#E6D6FF",
                          fontSize: 18,
                          opacity: hasWeight ? 1 : 0.45,
                        }}
                      />
                    ) : (
                      <StarOutlined
                        style={{
                          color: hasWeight ? "#A072EB" : "#E6D6FF",
                          fontSize: 18,
                          opacity: hasWeight ? 1 : 0.45,
                        }}
                      />
                    )
                  }
                />
              );
            },
          },
        ]}
        dataSource={getVisibleNodes(flatRecursiveData, expandedIds)}
        size="middle"
        pagination={false}
      />
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={totalCount}
        onChange={(page) => setCurrentPage(page)}
        style={{ marginTop: "1rem", textAlign: "right" }}
      />
      {/* {showCodeDetails && (
        <div className="codeDetailsContainer" style={{ marginTop: "8rem" }}>
          <h2 className="title">Data Snapshot</h2>
          <TabsView
            projectId={projectId}
            onLoadingChange={setIsSnapshotLoading}
          />
        </div>
      )} */}
      {showCodeDetails && (
        <div
          ref={snapshotRef}
          className="codeDetailsContainer"
          style={{ marginTop: "8rem" }}
        >
          <h2 className="title">Data Snapshot</h2>
          <TabsView projectId={projectId} />
        </div>
      )}
    </div>
  );
};

export default RecursiveCodeTable;
